/**
 * Load More Functionality
 * Handles AJAX loading of additional products/articles with skeleton screens
 */

class LoadMore {
  constructor(container) {
    this.container = container;
    this.button = container.querySelector('[data-load-more-button]');
    this.skeletonContainer = container.querySelector(
      '[data-skeleton-container]'
    );
    this.errorContainer = container.querySelector('[data-load-more-error]');
    this.retryButton = container.querySelector('[data-retry-button]');

    this.totalPages = parseInt(container.dataset.totalPages, 10);
    this.currentPage = parseInt(container.dataset.currentPage, 10);
    this.gridId = container.dataset.gridId;
    this.type = container.dataset.type;
    this.grid = document.getElementById(this.gridId);

    this.isLoading = false;
    this.nextUrl = this.button?.dataset.nextUrl;

    this.init();
  }

  init() {
    if (this.button) {
      this.button.addEventListener('click', () => this.loadMore());
    }

    if (this.retryButton) {
      this.retryButton.addEventListener('click', () => this.loadMore());
    }
  }

  async loadMore() {
    if (this.isLoading || !this.nextUrl) {
      return;
    }

    this.isLoading = true;
    this.showLoading();
    this.hideError();

    try {
      const response = await fetch(this.nextUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract new items from the fetched page
      const newGrid = doc.getElementById(this.gridId);
      const newItems = newGrid ? newGrid.children : [];

      if (newItems.length > 0) {
        // Append new items to existing grid
        Array.from(newItems).forEach(item => {
          this.grid.appendChild(item.cloneNode(true));
        });

        // Update current page
        this.currentPage += 1;

        // Update URL using History API
        const url = new URL(this.nextUrl, window.location.origin);
        window.history.pushState({}, '', url.pathname + url.search);

        // Update next URL
        const nextPageButton = doc.querySelector('[data-load-more-button]');

        if (nextPageButton && this.currentPage < this.totalPages) {
          this.nextUrl = nextPageButton.dataset.nextUrl;
          this.button.dataset.nextUrl = this.nextUrl;
        } else {
          // No more pages - just hide the button
          this.nextUrl = null;
          if (this.button) {
            this.button.classList.add('hidden');
          }
        }

        // Announce to screen readers
        this.announceNewItems(newItems.length);
      } else if (this.button) {
        this.button.classList.add('hidden');
      }
    } catch (error) {
      console.error('Load more error:', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  showLoading() {
    if (this.button) {
      this.button.disabled = true;
      this.button.querySelector('[data-button-text]').classList.add('hidden');
      this.button
        .querySelector('[data-button-loading]')
        .classList.remove('hidden');
    }

    if (this.skeletonContainer) {
      this.skeletonContainer.classList.remove('hidden');
      this.skeletonContainer.classList.add(
        'grid',
        'grid-cols-2',
        'lg:grid-cols-4',
        'gap-4',
        'lg:gap-6'
      );
    }
  }

  hideLoading() {
    if (this.button) {
      this.button.disabled = false;
      this.button
        .querySelector('[data-button-text]')
        .classList.remove('hidden');
      this.button
        .querySelector('[data-button-loading]')
        .classList.add('hidden');
    }

    if (this.skeletonContainer) {
      this.skeletonContainer.classList.add('hidden');
      this.skeletonContainer.classList.remove(
        'grid',
        'grid-cols-2',
        'lg:grid-cols-4',
        'gap-4',
        'lg:gap-6'
      );
    }
  }

  showError() {
    if (this.errorContainer) {
      this.errorContainer.classList.remove('hidden');
    }

    if (this.button) {
      this.button.classList.add('hidden');
    }
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.classList.add('hidden');
    }
  }

  announceNewItems(count) {
    // Create a live region announcement for screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';

    const typeTranslation = this.type === 'products' ? 'Produkte' : 'Artikel';
    announcement.textContent = `${count} weitere ${typeTranslation} wurden geladen`;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }
}

// Initialize all load more containers on page load
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[data-load-more-container]');
  containers.forEach(container => {
    window.loadMore = new LoadMore(container);
  });
});

// Re-initialize after AJAX filter updates (if using AJAX filters)
document.addEventListener('collection:filtered', () => {
  const containers = document.querySelectorAll('[data-load-more-container]');
  containers.forEach(container => {
    window.loadMore = new LoadMore(container);
  });
});
